import sanitizeHtml from 'sanitize-html';
import cookie, { CookieSerializeOptions } from 'cookie';

import { LAZY_TIMEOUT } from 'constants/appConstants';
import { EXPIRE_IMMEDIATE_COOKIE_TIME } from 'constants/cookies';
import memoize from 'helpers/memoize';
import { INTERNET_EMAIL, POPUP_CLASS_RE, RELATIVE_URL_RE } from 'common/regex';
import marketplace from 'cfg/marketplace.json';
const { msaImagesUrl } = marketplace;

const INVALID_ATTRIBUTE_CHARS = /[%\\]/;
// Check for `%` encoding in places outside of the querystring

const isAttributeInvalid = (attr?: string) => !attr || INVALID_ATTRIBUTE_CHARS.test(attr.split('?')[0] as string);

const FORCE_WEBP_STRING = '_FMwebp_';
/*
  CONVERT ASCII TO HEX VALUE FOR DISPLAYING FROM API
*/
export function makeAscii(str?: string) {
  return str?.replace(/&#(\d+);+/gi, (m, a) => String.fromCharCode(a));
}

/*
  Massage the given search term to be appropriate to put in a query string.
*/
export function makeQueryStringSearchTerm(term: string) {
  return term
    .trim()
    .replace(/\s+|\+{1,}/g, '+')
    .toLowerCase();
}

export function makeHashFromString(s: string, prefix = 'mar') {
  return (
    prefix +
    s.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0)
  );
}

/*
  Sanitizes an html string and helps filter out xss vulnerabilities
  Note: this function by default strips out certain tags (eg. img), the default list of allowed tags
    and instructions to modify can be found here: https://www.npmjs.com/package/sanitize-html

  allowedTags is a combination of sanitize-html's defaults, as well as ours. We have commented out a few defaults to prevent our SEO from being dynamically changed
  https://github.com/apostrophecms/sanitize-html/blob/f6c26208e1b5fc79757bfaae4dbfba0c1f9b6a11/index.js#L713
*/

const allowedTags = [
  'address',
  'article',
  'aside',
  'footer',
  'header',
  // 'h1', commented out to prevent our SEO /Accessibility from being manipulated by dynamic content
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hgroup',
  'main',
  'nav',
  'section',
  'blockquote',
  'dd',
  'div',
  'dl',
  'dt',
  'figcaption',
  'figure',
  'hr',
  'li',
  'main',
  'ol',
  'p',
  'pre',
  'ul',
  'a',
  'abbr',
  'b',
  'bdi',
  'bdo',
  'br',
  'cite',
  'code',
  'data',
  'dfn',
  'em',
  'i',
  'kbd',
  'mark',
  'q',
  'rb',
  'rp',
  'rt',
  'rtc',
  'ruby',
  's',
  'samp',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'time',
  'u',
  'var',
  'wbr',
  'caption',
  'col',
  'colgroup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  /*
    our own custom tags added to the default list
  */
  'img',
  'del'
];
export function cleanUp(dirtyHtml: string) {
  return sanitizeHtml(dirtyHtml, {
    allowedTags,
    allowedAttributes: {
      'a': ['href', 'target', 'data-application-action'],
      'h2': ['id'],
      'div': ['id'],
      'img': ['src', 'alt', 'title'],
      'th': ['scope'],
      '*': ['aria-hidden', 'aria-label']
    },
    allowedClasses: {
      'div': ['table-container'],
      'table': ['m-comparisonTable', 'm-comparison-table'],
      'td': ['yes', 'no'],
      '*': ['sr-only']
    },
    allowedSchemes: ['http', 'https', 'mailto', 'tel', 'sms'],
    transformTags: {
      // should strip out links with invalid hrefs
      a: (tagName, attribs) => {
        if (isAttributeInvalid(attribs.href)) {
          delete attribs.href;
        }

        return {
          tagName,
          attribs
        };
      }
    },
    exclusiveFilter: frame => frame.tag === 'img' && isAttributeInvalid(frame.attribs.src) // should remove image tags with invalid src attributes
  });
}

/*
  SET cookies
  (COOKIE_NAME, COOKIE_VALUE, COOKIE_OPTIONS_OBJECT)
*/
export function setCookie(name: string, value: string, options?: CookieSerializeOptions, doc = document) {
  doc.cookie = cookie.serialize(name, value, options);
}

/*
  REMOVE cookies
  (COOKIE_NAME)
*/
export function removeCookie(name: string, domain?: string, doc = document) {
  doc.cookie = cookie.serialize(name, '', {
    domain,
    path: '/',
    expires: new Date(EXPIRE_IMMEDIATE_COOKIE_TIME)
  });
}

/**
 * Strip string of special characters and consolidate whitespace
 * @param  {string} text String to clean up
 * @return {string}      cleaned string
 */
export function stripSpecialCharsConsolidateWhitespace(text: string) {
  return text
    .replace(/[^0-9a-z-_ ]/gi, '')
    .replace(/-+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^\s+/, '')
    .replace(/\s+$/, '');
}

/*
  Strip string of special characters and whitespace
*/
export function stripSpecialChars(text?: string | null) {
  return text ? text.replace(/[^\w]/gi, '') : text;
}

/*
  Strip string of special characters and replace with '-'
  mainly used to ready text for analytics to parse
*/
export function stripSpecialCharsDashReplace(text?: string) {
  return text ? text.trim().replace(/[^a-z0-9]+/gi, '-') : text;
}

/**
 * Sanitizes string for sending as an event payload- trims whitespace, replaces apostrophes, and converts whitespace to dashes
 */
export function sanitizeForEvent(string = '') {
  return string.trim().replace(/'/g, '').replace(/\s/g, '-');
}

/**
 * Check whether url passed in is an absolute url or not
 * @param {string}
 */
export function checkIfRelativeUrl(url: string) {
  return RELATIVE_URL_RE.test(url);
}

/**
 * Capitalize the first letter in a string. Does not alter the rest of the string.
 * @param str {string} the string you want capitalized
 * @return    {string} the string you passed in with the first character capital
 */
export const capitalize = (str: string) => {
  if (str && str.length >= 0) {
    return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
  }
  return;
};

/**
 * Makes the first character of a string uppercase and lowercases the rest of it.
 * @param str {string} the input string
 * @return    {string} the output string
 */
export const lowercaseThenCapitalize = (str?: string) => {
  if (str && str.length >= 0) {
    return `${str.charAt(0).toUpperCase()}${str.slice(1).toLowerCase()}`;
  }
  return;
};

export const indefiniteArticleSelector = (str?: string) => {
  if (str && /^\w/.test(str)) {
    return /^[aeiou]/.test(str.charAt(0)) ? 'an' : 'a';
  }

  return;
};

/**
 * Pluralize a word
 * @param str string to pluralize
 * @param count how many items
 * @param revert return singular form of a plural input
 */
export const pluralize = memoize((str: string, count: number, revert: boolean) => {
  if (count === 1) {
    return str;
  }

  const plural: { [key: string]: string } = {
    '(quiz)$': '$1zes',
    '^(ox)$': '$1en',
    '([m|l])ouse$': '$1ice',
    '(matr|vert|ind)ix|ex$': '$1ices',
    '(x|ch|ss|sh)$': '$1es',
    '([^aeiouy]|qu)y$': '$1ies',
    '(hive)$': '$1s',
    '(?:([^f])fe|([lr])f)$': '$1$2ves',
    '(shea|lea|loa|thie)f$': '$1ves',
    'sis$': 'ses',
    '([ti])um$': '$1a',
    '(tomat|potat|ech|her|vet)o$': '$1oes',
    '(bu)s$': '$1ses',
    '(alias)$': '$1es',
    '(octop)us$': '$1i',
    '(ax|test)is$': '$1es',
    '(us)$': '$1es',
    '([^s]+)$': '$1s'
  };

  const singular: { [key: string]: string } = {
    '(quiz)zes$': '$1',
    '(matr)ices$': '$1ix',
    '(vert|ind)ices$': '$1ex',
    '^(ox)en$': '$1',
    '(alias)es$': '$1',
    '(octop|vir)i$': '$1us',
    '(cris|ax|test)es$': '$1is',
    '(shoe)s$': '$1',
    '(o)es$': '$1',
    '(bus)es$': '$1',
    '([m|l])ice$': '$1ouse',
    '(x|ch|ss|sh)es$': '$1',
    '(m)ovies$': '$1ovie',
    '(s)eries$': '$1eries',
    '([^aeiouy]|qu)ies$': '$1y',
    '([lr])ves$': '$1f',
    '(tive)s$': '$1',
    '(hive)s$': '$1',
    '(li|wi|kni)ves$': '$1fe',
    '(shea|loa|lea|thie)ves$': '$1f',
    '(^analy)ses$': '$1sis',
    '((a)naly|(b)a|(d)iagno|(p)arenthe|(p)rogno|(s)ynop|(t)he)ses$': '$1$2sis',
    '([ti])a$': '$1um',
    '(n)ews$': '$1ews',
    '(h|bl)ouses$': '$1ouse',
    '(corpse)s$': '$1',
    '(us)es$': '$1',
    's$': ''
  };

  const irregular: { [key: string]: string } = {
    move: 'moves',
    foot: 'feet',
    goose: 'geese',
    sex: 'sexes',
    child: 'children',
    man: 'men',
    tooth: 'teeth',
    person: 'people',
    this: 'these',
    that: 'those',
    criterion: 'criteria'
  };

  const uncountable = [
    'sheep',
    'fish',
    'deer',
    'moose',
    'series',
    'species',
    'media',
    'money',
    'rice',
    'information',
    'equipment',
    'garbage',
    'you',
    'wildlife',
    'staff'
  ];

  // Save some time in the case that singular and plural are the same
  if (uncountable.indexOf(str.toLowerCase()) >= 0) {
    return str;
  }

  // Check for irregular forms
  for (const word in irregular) {
    let pattern: RegExp;
    let replace: string;
    if (revert) {
      pattern = new RegExp(irregular[word] + '$', 'i');
      replace = word;
    } else {
      pattern = new RegExp(word + '$', 'i');
      replace = irregular[word] || '';
    }
    if (pattern.test(str)) {
      return str.replace(pattern, replace);
    }
  }

  let array;
  if (revert) {
    array = singular;
  } else {
    array = plural;
  }

  // Check for matches using regular expressions
  for (const reg in array) {
    if (array.hasOwnProperty(reg)) {
      const pattern = new RegExp(reg, 'i');
      const val = array[reg] || '';
      if (pattern.test(str)) {
        return str.replace(pattern, val);
      }
    }
  }

  return str;
});

/**
 * Opens new popup with given width/height dimensions from data-popup-options attribute
 * Example: <a href="" data-popup-options="width=400,height=600" onClick={openPopup}>Link</a>
 * @param event - click event
 * @param win - global window object
 * @returns {boolean}
 */
export const openPopup = (event: React.MouseEvent<HTMLAnchorElement>, win = window) => {
  event.preventDefault();
  const { currentTarget, target: t } = event;
  const target = t as HTMLAnchorElement;
  let href;
  let popupOptions;
  if (!currentTarget) return;
  const dataOptions = currentTarget.getAttribute('data-popup-options');
  if (dataOptions) {
    ({ href } = currentTarget);
    popupOptions = dataOptions;
  } else if (target && POPUP_CLASS_RE.test(target.className)) {
    const matches = target.className.match(POPUP_CLASS_RE);
    ({ href } = target);
    if (matches) {
      popupOptions = `width=${matches[1]},height=${matches[2]}`;
    }
  }
  if (href) {
    const popup = win.open(href, '_blank', popupOptions);
    popup?.focus();
    return false;
  }

  return;
};

/**
 * Checks that a dom element has a css class and if not, adds it.
 * @param {Element} el         dom element
 * @param {String}  className  css class to check and add if needed
 */
export function ensureClass(el: HTMLElement, className: string) {
  el.classList.contains(className) || el.classList.add(className);
}

/**
 * Checks that a dom element does NOT have a css class and if it does, removes it.
 * @param {Element} el         dom element
 * @param {String}  className  css class to check and remove if needed
 */
export function ensureNoClass(el: HTMLElement, className: string) {
  el.classList.contains(className) && el.classList.remove(className);
}

/**
 * Use requestIdleCallback (or a fallback) to lazily postpone non-priority code
 * @callback {function} callback method you want loaded lazily
 */
export const lazyExecute = (callback: IdleRequestCallback) => {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback);
    return;
  } else {
    const start = Date.now();
    return setTimeout(() => {
      callback({
        didTimeout: false,
        timeRemaining: function () {
          return Math.max(0, 50 - (Date.now() - start));
        }
      });
    }, LAZY_TIMEOUT);
  }
};

/**
 * Convert an arbitrary number of arguments which may or may not be arrays into
 * a single array.
 * @param  {array} args
 * @return {array}
 */
export const arrayify = (...args: never[]) =>
  args.reduce((acc, arg) => {
    const append = Array.isArray(arg) ? arg : [arg];
    return acc.concat(append);
  }, []);

/**
 * Check if the page refferrer is external, i.e. navigated to Marty from an external site
 * @returns {boolean}
 */
export const isExternalReferrer = (win = window) => {
  if (typeof win.document === 'undefined') {
    return false;
  }
  return !win.document.referrer.includes(marketplace.siteDomain);
};

/**
 * Generate MSA image url
 * API: https://confluence.zappos.net/display/IN/Cloud+Catalog+API#CloudCatalogAPI-MSAimageinformation
 *
 * @param imageId          {string}        the MSA image ID
 * @param msaOpts          {object}        see confluence article above
 *        msaOpts.autoCrop {boolean}       see confluence article above
 *        msaOpts.width    {string|number} see confluence article above
 *        msaOpts.height   {string|number} see confluence article above
 * @param baseUrl          {string}        override the base URL of MSA
 * @return {string}
 */
const defaultMsaOpts: MSAOpts = {
  extension: 'jpg',
  customSettings: '',
  minWidth: 300
};

// can be expanded to use more opts based on whats available in MSA. We currently only use these
type MSAOpts = {
  autoCrop?: boolean;
  height?: number;
  width?: number;
  customSettings?: string;
  useWebp?: boolean;
  extension?: string;
  minWidth?: number; // used for computing srcsets
};

export const constructMSAImageUrl = (imageId?: string, msaOpts?: MSAOpts, baseUrl = msaImagesUrl) => {
  if (!imageId) {
    return '';
  }
  const opts = { ...defaultMsaOpts, ...msaOpts };

  const autoCrop = opts.autoCrop ? '_AC' : '';
  let dimensions = '';
  if (opts.height && opts.width) {
    dimensions = `_SR${opts.width},${opts.height}`;
  } else if (opts.height) {
    dimensions = `_SY${opts.height}`;
  } else if (opts.width) {
    dimensions = `_SX${opts.width}`;
  }
  const codes = autoCrop || dimensions ? `.${autoCrop}${dimensions}_` : '';

  return `${baseUrl}${imageId}${codes}${opts.customSettings}${opts.useWebp ? FORCE_WEBP_STRING : ''}.${opts.extension}`;
};

/**
 * compute the widths needed for a srcset.
 *
 * @param min
 * @param max
 */
const widthDistro = memoize((min: number, max: number) => {
  const ret = [];
  // webp/jpg image sizes scale approx to the 1.666 power of a side (by observation)
  const bottom = Math.pow(min, 5 / 3);
  const top = Math.pow(max, 5 / 3);
  // This computes the number of steps (images) needed.
  // the 2 is actually  -1/log(sqrt(2)/2),  which corresponds to a
  // "target magnification step" of approx. (.707)  This was chosen
  // because the number is close to our target magnification and
  // also because it creates a nice even number
  let count = Math.floor(Math.log(top / bottom) * 2);

  // just evenly compute widths for now.  it seems effective enough
  const step = (max - min) / (count - 1);
  ret.push(max);
  count = Math.floor(count) - 1;
  while (count > 1) {
    count--;
    ret.push(Math.floor(count * step + min));
  }
  ret.push(min);
  return ret;
});

/**
 * Construct a srcset for an MSA image
 *
 * @param imageId msa image ID
 * @param msaOpts the "msaOpts"
 * @param baseUrl base URL to use. - defaults to public MSA endpoint
 */
export const constructMSASrcset = (imageId?: string, msaOpts?: MSAOpts, baseUrl = msaImagesUrl) => {
  if (!imageId) {
    return undefined;
  }

  const opts = { ...defaultMsaOpts, ...msaOpts };
  if (!opts.width) {
    // we can not compute the source set - just use the old DPR based format.
    const retinaUrl = constructMSAImageUrl(imageId, opts, baseUrl);
    const stdOpts = {
      ...opts,
      width: opts.width && Math.floor(opts.width / 2),
      height: opts.height && Math.floor(opts.height / 2)
    };
    const stdUrl = constructMSAImageUrl(imageId, stdOpts, baseUrl);
    return `${stdUrl} x1, ${retinaUrl} x2`;
  }

  let ret = '';
  for (const w of widthDistro(opts.minWidth || 300, opts.width)) {
    const currentOpts = {
      ...opts,
      width: w,
      height: opts.height && Math.floor((opts.height * w) / opts.width)
    };
    const url = constructMSAImageUrl(imageId, currentOpts, baseUrl);
    ret += `${ret.length !== 0 ? ', ' : ''}${url} ${currentOpts.width}w`;
  }
  return ret;
};

export const originalLayeredMsaValues = {
  botH: 1200,
  botW: 1200,
  botX: 50,
  botY: 50,
  topH: 1300,
  topW: 1300,
  topX: 0,
  topY: 0
};
// Take number that is new h/w dimension. ex: 50
// Return obj w/ scaled measurements for other positioning
export const constructLayeredMsaImageSizingPositioning = (dimension: number) =>
  Object.entries(originalLayeredMsaValues).reduce((acc: { [key: string]: number }, [key, value]) => {
    acc[key] = Math.round((dimension * value) / originalLayeredMsaValues.botH);
    return acc;
  }, {});

type LayeredMSAImageUrlArgs = {
  topImageId: string;
  botImageId: string;
  versionNumber?: string;
  botH: string;
  botW: string;
  botX: string;
  botY: string;
  topH: string;
  topW: string;
  topX: string;
  topY: string;
  extension?: string;
};

/**
 * Converts to png imageIds to a single layered jpg
 * jpg is much lighter than png
 */
export const constructLayeredMsaImageUrl = ({
  topImageId,
  botImageId,
  versionNumber = 'a', // arbitrary ID
  botH,
  botW,
  botX,
  botY,
  topH,
  topW,
  topX,
  topY,
  extension = 'png'
}: LayeredMSAImageUrlArgs) => {
  const opts = {
    customSettings: `._CL${versionNumber}|${topW},${topH}|${topImageId}.png|${botX},${botY},${botW},${botH}+${topX},${topY},${topW},${topH}_FMjpg`,
    extension
  };
  return constructMSAImageUrl(botImageId, opts);
};

export const combineSideEffects =
  (...fns: unknown[]) =>
  (...args: unknown[]) => {
    fns.forEach(fn => {
      fn && typeof fn === 'function' && fn(...args);
    });
  };

export const checkJSEnabled = () => ensureClass(document.body, 'jsEnabled');

/**
 * JS modulus operator (%) doesn't work "correctly" for negative bases,
 * e.g.  -1 % 5 => -1, where the true math answer would be -1 % 5 => 4
 * This function gives us a true modulo answer.
 * @param a number left hand side of modulus
 * @param b number right hand side of modulus
 * @returns Number
 */
export const mod = (a: number, b: number) => ((a % b) + b) % b;

/**
 * Just a NO OPERATION function where a function handler is required but is otherwised unused.
 * @return {null}
 */

export const noop = () => null;

export const isValidEmailAddress = (emailAddress: string) => (emailAddress.match(INTERNET_EMAIL) || []).length === 1;

export const isValidVerificationCode = (code: string) => code.length === 6;
